import { useInfiniteQuery, useQuery, type InfiniteData } from "@tanstack/react-query";
import { linkOptions } from "@tanstack/react-router";
import type {
  UserSettings,
  WatchHistoryEntry,
  WatchHistoryResponse,
  WatchStats,
} from "@rago/web-shared";
import { seasonEpisodePair } from "../components/library/media-meta";
import { getSources } from "../sources";
import { tmdbImageUrl } from "../sources/discoverySource";
import { ServerSource } from "../sources/serverSource";
import { api } from "./apiClient";
import { exactCount } from "./formatNumber";
import { monthYear } from "./timeAgo";

/**
 * The account profile's data: the "Sync my watch state" consent (Settings ›
 * Account), the cloud watch history it feeds and the "watched since
 * joining" stats — plus the pure helpers the profile pages render with.
 *
 * Three cache keys of their own. ["me","settings"] belongs to the Quality
 * and Player pages, which keep normalized PlayerPrefs under it and write it
 * back with setQueryData — so even though the consent rides the same
 * /v1/me/settings document, it is never read through that key.
 */
export const WATCH_STATE_SYNC_KEY = ["me", "watch-state-sync"] as const;
export const WATCH_HISTORY_KEY = ["me", "watch-history"] as const;
export const WATCH_STATS_KEY = ["me", "watch-stats"] as const;

export interface WatchStateSync {
  enabled: boolean;
  /** When the consent was last switched on (the "On since …" hint); null while off. */
  since: string | null;
}

/** Defensive read of the settings document: a broker without the field reads as off. */
export function normalizeWatchStateSync(raw: unknown): WatchStateSync {
  const r = (raw ?? {}) as Partial<UserSettings> & Record<string, unknown>;
  const enabled = r.watchStateSync === true;
  const since = enabled && typeof r.watchStateSyncAt === "string" ? r.watchStateSyncAt : null;
  return { enabled, since };
}

export function useWatchStateSync() {
  return useQuery({
    queryKey: WATCH_STATE_SYNC_KEY,
    queryFn: async () => normalizeWatchStateSync(await api<UserSettings>("/v1/me/settings")),
    staleTime: 60_000,
  });
}

/** PATCH the consent alone; the broker returns the whole settings document. */
export async function saveWatchStateSync(enabled: boolean): Promise<WatchStateSync> {
  const res = await api<UserSettings>("/v1/me/settings", {
    method: "PATCH",
    body: { watchStateSync: enabled },
  });
  return normalizeWatchStateSync(res);
}

/**
 * Ask every online server to re-sync its grants now. The broker stamps the
 * consent on the account's grants when it flips, but a server only reads
 * them on its heartbeat (up to 5 min) — long enough to play something,
 * open the profile and see nothing. Fire-and-forget: an offline server
 * catches up on its own beat, and a failed nudge changes nothing the
 * heartbeat will not fix.
 */
export function nudgeServerGrants(): void {
  for (const source of getSources()) {
    if (source instanceof ServerSource) {
      void source.conn.request("/v1/grants/refresh", { method: "POST" }).catch(() => {});
    }
  }
}

/** How long after a nudge the servers' backfill upload has typically landed. */
export const SYNC_SETTLE_MS = 6_000;

export const HISTORY_PAGE_SIZE = 24;

/** `/v1/me/watch-history?limit=24[&cursor=…]` — keyset paging, newest first. */
export function historyPath(cursor: string | null, limit = HISTORY_PAGE_SIZE): string {
  const q = new URLSearchParams({ limit: String(limit) });
  if (cursor) q.set("cursor", cursor);
  return `/v1/me/watch-history?${q.toString()}`;
}

export function useWatchHistory() {
  return useInfiniteQuery({
    queryKey: WATCH_HISTORY_KEY,
    queryFn: ({ pageParam }) => api<WatchHistoryResponse>(historyPath(pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 60_000,
    // Cached pages paint instantly; a scrobble since the last visit shows
    // on this one rather than after the stale window.
    refetchOnMount: "always",
  });
}

export function flattenHistory(
  data: InfiniteData<WatchHistoryResponse> | undefined,
): WatchHistoryEntry[] {
  return data ? data.pages.flatMap((p) => p.items) : [];
}

export function useWatchStats() {
  return useQuery({
    queryKey: WATCH_STATS_KEY,
    queryFn: () => api<WatchStats>("/v1/me/watch-stats"),
    staleTime: 60_000,
  });
}

/**
 * Where a history entry goes: the Discovery season page for an episode,
 * the Discovery detail page for a movie. Only tmdb-anchored entries carry
 * ids (Discovery is TMDB); a tvdb one renders without a link.
 */
export function historyLink(entry: WatchHistoryEntry) {
  if (entry.mediaType === "episode") {
    if (!entry.tmdb.tvId || entry.seasonNumber == null) return null;
    return linkOptions({
      to: "/discover/tv/$tmdbId/season/$seasonNumber",
      params: { tmdbId: entry.tmdb.tvId, seasonNumber: String(entry.seasonNumber) },
    });
  }
  if (!entry.tmdb.movieId) return null;
  return linkOptions({
    to: "/discover/$mediaType/$tmdbId",
    params: { mediaType: "movie", tmdbId: entry.tmdb.movieId },
  });
}

export interface HistoryCaption {
  primary: string;
  secondary: string | null;
  tertiary: string | null;
}

/** The card's three caption lines — episode: show / episode title / "S1 · E5"; movie: title / year. */
export function historyCaption(entry: WatchHistoryEntry): HistoryCaption {
  if (entry.mediaType === "episode") {
    const pair = seasonEpisodePair(entry.seasonNumber, entry.episodeNumber);
    if (entry.showTitle) {
      return { primary: entry.showTitle, secondary: entry.title, tertiary: pair || null };
    }
    return { primary: entry.title, secondary: pair || null, tertiary: null };
  }
  return { primary: entry.title, secondary: entry.year ? String(entry.year) : null, tertiary: null };
}

/** The list row's two lines — episode: show / "S1 · E5 · episode title"; movie: title / year. */
export function historyRowLines(entry: WatchHistoryEntry): { primary: string; secondary: string | null } {
  const { primary, secondary, tertiary } = historyCaption(entry);
  if (entry.mediaType === "episode" && entry.showTitle) {
    return { primary, secondary: [tertiary, secondary].filter(Boolean).join(" · ") || null };
  }
  return { primary, secondary };
}

/**
 * Art for a 16:9 frame: an episode's still (TMDB cuts stills at w300 and
 * original only), else the title's backdrop — never a poster, which would
 * be cropped to a strip.
 */
export function historyArtUrl(entry: WatchHistoryEntry, variant: "card" | "row"): string | null {
  const card = variant === "card";
  if (entry.mediaType === "episode" && entry.art.stillPath) {
    return tmdbImageUrl(entry.art.stillPath, card ? "original" : "w300");
  }
  return tmdbImageUrl(entry.art.backdropPath, card ? "w780" : "w300");
}

/** "Joined Sep 2026"; null when the session predates the createdAt field. */
export function joinedLabel(createdAt: string | null | undefined, locale?: string): string | null {
  if (!createdAt) return null;
  const label = monthYear(createdAt, locale);
  return label ? `Joined ${label}` : null;
}

/** The tile letter: the name's first character, upper-cased ("" for a blank name). */
export function initialOf(name: string): string {
  const first = [...name.trim()][0];
  return first ? first.toUpperCase() : "";
}

/** "8 titles" — a profile rail's inline count. */
export function titlesLabel(n: number, locale?: string): string {
  return `${exactCount(n, locale)} ${n === 1 ? "title" : "titles"}`;
}

export interface HistoryDayGroup {
  key: string;
  label: string;
  entries: WatchHistoryEntry[];
}

const DAY_MS = 86_400_000;

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function dayLabel(d: Date, daysAgo: number, today: Date, locale?: string): string {
  if (daysAgo <= 0) return "Today";
  if (daysAgo === 1) return "Yesterday";
  if (daysAgo < 7) return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(d);
  const opts: Intl.DateTimeFormatOptions = { month: "long", day: "numeric" };
  if (d.getFullYear() !== today.getFullYear()) opts.year = "numeric";
  return new Intl.DateTimeFormat(locale, opts).format(d);
}

/**
 * The history page's day buckets, in the entries' own (newest-first)
 * order, by LOCAL calendar day: Today, Yesterday, the weekday inside the
 * last week, then "September 1" (with the year once it differs). Day
 * distance is measured between local midnights, so a DST change never
 * shifts a bucket; a stamp from the future (a server clock ahead of ours)
 * and an unreadable one both file under Today rather than vanish.
 */
export function groupHistoryByDay(
  entries: readonly WatchHistoryEntry[],
  now: number = Date.now(),
  locale?: string,
): HistoryDayGroup[] {
  const groups: HistoryDayGroup[] = [];
  const today = new Date(now);
  const todayStart = startOfLocalDay(today);
  for (const e of entries) {
    let d = new Date(e.watchedAt);
    if (!Number.isFinite(d.getTime())) d = today;
    const daysAgo = Math.round((todayStart - startOfLocalDay(d)) / DAY_MS);
    const key =
      daysAgo <= 0
        ? "today"
        : daysAgo === 1
          ? "yesterday"
          : `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    const last = groups.at(-1);
    if (last && last.key === key) {
      last.entries.push(e);
      continue;
    }
    groups.push({ key, label: dayLabel(d, daysAgo, today, locale), entries: [e] });
  }
  return groups;
}
